import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronUp, ChevronDown, Menu } from 'lucide-react';

const TermosDeUso = () => {
  const [showSumario, setShowSumario] = useState(false);

  const sections = [
    { id: 'preambulo', title: 'Preâmbulo' },
    { id: 'definicoes', title: '1. Definições' },
    { id: 'cadastro', title: '2. Cadastro e Acesso à Plataforma' },
    { id: 'planos', title: '3. Planos, Assinatura e Pagamentos' },
    { id: 'funcionalidades', title: '4. Funcionalidades e Níveis de Serviço' },
    { id: 'obrigacoes', title: '5. Obrigações e Responsabilidades do Usuário' },
    { id: 'propriedade', title: '6. Propriedade Intelectual' },
    { id: 'privacidade', title: '7. Privacidade e Proteção de Dados (LGPD)' },
    { id: 'limitacoes', title: '8. Limitações de Responsabilidade e Garantias' },
    { id: 'vigencia', title: '9. Vigência, Suspensão e Rescisão' },
    { id: 'cancelamento', title: '10. Cancelamento de Eventos pelo Cliente Final' },
    { id: 'alteracoes', title: '11. Alterações nos Termos' },
    { id: 'disposicoes', title: '12. Disposições Gerais' },
    { id: 'aceite', title: '13. Aceite e Consentimento' },
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowSumario(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title="Termos de Uso — AmeUp"
        description="Termos de Uso e Condições Gerais de Serviço da plataforma AmeUp, SaaS B2B para Gestão de Buffets e Eventos."
        canonical="https://ameup.com.br/termos-de-uso"
      />
      <Navbar />

      {/* Sticky Sumário */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b">
        <div className="container mx-auto max-w-4xl px-4 py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSumario(!showSumario)}
            className="flex items-center gap-2 hover:bg-primary/10"
          >
            <Menu className="h-4 w-4" />
            Sumário
            {showSumario ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>

          {showSumario && (
            <div className="mt-2 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <button
                onClick={() => scrollToSection('preambulo')}
                className="w-full text-left p-2 hover:bg-primary/10 rounded text-sm transition-colors text-foreground hover:text-primary font-semibold mb-2 border-b border-primary/10 pb-2"
              >
                Preâmbulo
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {sections.filter(s => s.id !== 'preambulo').map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="text-left p-2 hover:bg-primary/10 rounded text-sm transition-colors text-muted-foreground hover:text-primary font-medium"
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="flex-grow py-16 bg-gray-50">
        <div className="container mx-auto max-w-4xl px-4">
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5">
              <h1 className="text-3xl font-bold text-center text-gray-800">
                Termos de Uso e Condições Gerais de Serviço
              </h1>
              <p className="text-center text-gray-600 mt-1 font-medium">
                Plataforma SaaS B2B para Gestão de Buffets e Eventos
              </p>
              <p className="text-center text-gray-500 mt-2 text-sm">
                Versão 1.0 — Vigência a partir de março de 2026
              </p>
            </CardHeader>

            <CardContent className="prose max-w-none p-8">
              <div className="space-y-8">

                {/* Preâmbulo */}
                <section id="preambulo" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">Preâmbulo</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Estes Termos de Uso e Condições Gerais de Serviço ("Termos") regulam a relação contratual entre a <strong>AMEUP TECNOLOGIA LTDA.</strong>, empresa com sede no Brasil, inscrita no CNPJ sob nº 59.792.355-0001/03, com endereço eletrônico www.ameup.com.br ("AmeUp", "nós", "nos" ou "nosso"), e a pessoa jurídica ou profissional autônomo que se cadastra e utiliza a plataforma AmeUp ("Usuário", "Fornecedor", "você" ou "seu").
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    Ao se cadastrar, acessar ou utilizar qualquer funcionalidade da plataforma AmeUp, você concorda integralmente com estes Termos, com a Política de Privacidade e com quaisquer políticas complementares publicadas pela AmeUp. Caso não concorde com qualquer disposição, não utilize a plataforma.
                  </p>
                  <p className="text-gray-700 leading-relaxed mt-3">
                    A AmeUp é uma solução tecnológica SaaS (Software as a Service) voltada exclusivamente para fornecedores do setor de eventos — especialmente buffets, padarias, confeitarias, empresas de decoração e prestadores de serviços correlatos — com o objetivo de digitalizar, automatizar e profissionalizar sua operação comercial.
                  </p>
                </section>

                {/* 1. Definições */}
                <section id="definicoes" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">1. Definições</h2>
                  <p className="text-gray-700 mb-4">Para fins destes Termos, aplicam-se as seguintes definições:</p>
                  <ul className="list-disc pl-6 space-y-3 text-gray-700">
                    <li><strong>Plataforma:</strong> o conjunto de sistemas, softwares, APIs, interfaces web e aplicativos disponibilizados pela AmeUp para acesso via internet.</li>
                    <li><strong>Usuário:</strong> pessoa jurídica ou profissional autônomo, devidamente cadastrado, que contrata e utiliza os serviços da Plataforma no exercício de sua atividade empresarial ou profissional.</li>
                    <li><strong>Cliente Final:</strong> pessoa física ou jurídica atendida pelo Usuário por meio da Plataforma, para fins de contratação de serviços de eventos.</li>
                    <li><strong>Conta:</strong> perfil individual criado pelo Usuário no momento do cadastro, por meio do qual acessa as funcionalidades da Plataforma.</li>
                    <li><strong>Plano:</strong> modalidade de assinatura escolhida pelo Usuário (Comece, Cresça, Multiplique ou Star), conforme descrito no site da AmeUp.</li>
                    <li><strong>GMV (Gross Merchandise Value):</strong> volume total de vendas intermediadas ou registradas pelo Usuário por meio da Plataforma.</li>
                    <li><strong>Comissão:</strong> percentual sobre o GMV devido à AmeUp, conforme o Plano contratado.</li>
                    <li><strong>Conteúdo do Usuário:</strong> todas as informações, dados, imagens, cardápios, contratos, preços e demais materiais inseridos pelo Usuário na Plataforma.</li>
                  </ul>
                </section>

                {/* 2. Cadastro e Acesso */}
                <section id="cadastro" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">2. Cadastro e Acesso à Plataforma</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">2.1 Elegibilidade</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Para utilizar a Plataforma, o Usuário deve: (i) ser maior de 18 anos ou estar devidamente representado por seu responsável legal; (ii) possuir capacidade civil plena para celebrar contratos; (iii) exercer atividade empresarial ou profissional no setor de eventos ou correlatos; e (iv) fornecer informações verídicas, precisas e atualizadas no cadastro.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">2.2 Processo de Cadastro</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O cadastro é realizado mediante o preenchimento do formulário disponível na Plataforma, com informações como razão social ou nome completo, CNPJ ou CPF, endereço, telefone e e-mail. O Usuário é integralmente responsável pela veracidade e atualização dessas informações.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">2.3 Segurança da Conta</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário é o único responsável pela guarda e confidencialidade de suas credenciais de acesso (login e senha). Qualquer acesso realizado com as credenciais do Usuário será de sua exclusiva responsabilidade. Em caso de suspeita de acesso não autorizado, o Usuário deve notificar imediatamente a AmeUp pelo canal de suporte oficial.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">2.4 Conta Única</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Cada Usuário pode manter apenas uma Conta ativa na Plataforma, salvo quando expressamente autorizado pela AmeUp por escrito. A criação de contas duplicadas ou fictícias é vedada e pode resultar no cancelamento de todas as contas envolvidas.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 3. Planos, Assinatura e Pagamentos */}
                <section id="planos" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">3. Planos, Assinatura e Pagamentos</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">3.1 Modalidades de Planos</h3>
                      <p className="text-gray-700 mb-3">A AmeUp oferece as seguintes modalidades de plano, sujeitas a alteração conforme comunicado com antecedência mínima de 30 (trinta) dias:</p>
                      <ul className="list-disc pl-6 space-y-2 text-gray-700">
                        <li><strong>Comece:</strong> gratuito, com comissão de 3% sobre cada venda realizada no menu digital;</li>
                        <li><strong>Cresça:</strong> R$ 59,90/mês, com comissão de 2% sobre cada venda realizada no menu digital;</li>
                        <li><strong>Multiplique:</strong> R$ 79,90/mês, com comissão de 1% sobre cada venda realizada no menu digital;</li>
                        <li><strong>Star:</strong> mediante convite, com comissão de 5% sobre cada venda realizada no marketplace, incluindo acesso ao Lead Marketplace.</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">3.2 Cobrança da Assinatura</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Os planos pagos são cobrados de acordo com o plano escolhido pelo Usuário, mensalmente ou anualmente, de forma antecipada, via cartão de crédito conforme opções disponíveis na Plataforma. A ausência de pagamento no prazo implica suspensão automática do acesso às funcionalidades do plano contratado, sem prejuízo da continuidade da cobrança do período inadimplido.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">3.3 Comissão sobre Vendas</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A comissão incide sobre o valor bruto de cada venda registrada ou processada por meio da Plataforma. A AmeUp poderá realizar a liquidação da comissão diretamente no momento do processamento do pagamento. O Usuário desde já autoriza tal desconto.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">3.4 Reajuste de Preços</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A AmeUp se reserva o direito de reajustar os valores dos planos periodicamente, mediante comunicação ao Usuário com antecedência mínima de 30 (trinta) dias. Caso o Usuário não concorde com o reajuste, poderá cancelar sua assinatura antes da entrada em vigor dos novos valores.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">3.5 Política de Reembolso</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Não são concedidos reembolsos por períodos já cobrados e utilizados, ainda que parcialmente. Em casos excepcionais devidamente comprovados, a AmeUp poderá, a seu exclusivo critério, oferecer créditos na Plataforma em substituição ao reembolso.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 4. Funcionalidades e Níveis de Serviço */}
                <section id="funcionalidades" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">4. Funcionalidades e Níveis de Serviço</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">4.1 Funcionalidades Disponíveis</h3>
                      <p className="text-gray-700 mb-3">A AmeUp disponibiliza, conforme o Plano contratado, as seguintes funcionalidades, entre outras:</p>
                      <ul className="list-disc pl-6 space-y-2 text-gray-700">
                        <li>Menu Digital e QR Code do Cardápio</li>
                        <li>Pagamento Online integrado</li>
                        <li>Central de Pedidos e Gestão de Pedidos</li>
                        <li>Gestão Financeira e Relatórios</li>
                        <li>Confecção de Contratos digitais</li>
                        <li>Emissão de Notas Fiscais (NF)</li>
                        <li>Mensageria Automatizada e Chat com Cliente</li>
                        <li>CRM de Clientes</li>
                        <li>Calendário e Agenda Integrada (com sincronização Google)</li>
                        <li>Ficha Técnica e Controle de Estoque</li>
                        <li>Calculadora e Precificação</li>
                        <li>URL Personalizado</li>
                        <li>Feedback pós-evento</li>
                        <li>Lead Marketplace (exclusivo para o Plano Star)</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">4.2 Disponibilidade</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A AmeUp envidará seus melhores esforços para manter a Plataforma disponível 24 (vinte e quatro) horas por dia, 7 (sete) dias por semana. Contudo, não garante disponibilidade ininterrupta, podendo ocorrer interrupções por motivos de manutenção programada, atualizações, falhas de terceiros ou casos fortuitos. Manutenções programadas serão comunicadas com antecedência sempre que possível.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">4.3 Integrações com Terceiros</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Algumas funcionalidades da Plataforma dependem de serviços de terceiros (ex.: Google Agenda, gateways de pagamento, emissores de NF). A AmeUp não se responsabiliza por falhas, interrupções ou alterações nesses serviços de terceiros, que seguem seus próprios termos e políticas.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 5. Obrigações e Responsabilidades do Usuário */}
                <section id="obrigacoes" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">5. Obrigações e Responsabilidades do Usuário</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">5.1 Uso Adequado</h3>
                      <p className="text-gray-700 mb-3">O Usuário obriga-se a utilizar a Plataforma exclusivamente para fins lícitos, de acordo com estes Termos, a legislação brasileira aplicável e as boas práticas do setor. É vedado ao Usuário:</p>
                      <ul className="list-disc pl-6 space-y-2 text-gray-700">
                        <li>Utilizar a Plataforma para fins fraudulentos ou ilícitos;</li>
                        <li>Inserir informações falsas, enganosas ou de terceiros sem autorização;</li>
                        <li>Tentar acessar sistemas, dados ou funcionalidades não autorizados;</li>
                        <li>Realizar engenharia reversa, copiar, modificar ou distribuir o código-fonte da Plataforma;</li>
                        <li>Utilizar robôs, scrapers ou qualquer mecanismo automatizado não autorizado;</li>
                        <li>Sobrecarregar intencionalmente os servidores da AmeUp;</li>
                        <li>Revender, sublicenciar ou ceder o acesso à Plataforma a terceiros sem autorização prévia e por escrito da AmeUp.</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">5.2 Conteúdo do Usuário</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário é integralmente responsável por todo o Conteúdo do Usuário inserido na Plataforma, incluindo cardápios, imagens, preços, contratos e dados de Clientes Finais. O Usuário declara e garante que: (i) possui todos os direitos necessários sobre o Conteúdo do Usuário; (ii) o Conteúdo do Usuário não viola direitos de terceiros; e (iii) o Conteúdo do Usuário está em conformidade com a legislação aplicável, incluindo a LGPD.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">5.3 Dados dos Clientes Finais</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Ao inserir dados de Clientes Finais na Plataforma, o Usuário declara que obteve todos os consentimentos necessários para o tratamento dessas informações, assumindo a responsabilidade pelo cumprimento das obrigações previstas na Lei Geral de Proteção de Dados (Lei 13.709/2018).
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">5.4 Obrigações Fiscais e Legais</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário é o único responsável pelo cumprimento de suas obrigações fiscais, trabalhistas, previdenciárias e legais decorrentes do exercício de sua atividade, não cabendo à AmeUp qualquer responsabilidade a esse respeito. A funcionalidade de emissão de NF é auxiliar e não substitui a responsabilidade fiscal do Usuário.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 6. Propriedade Intelectual */}
                <section id="propriedade" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">6. Propriedade Intelectual</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">6.1 Propriedade da AmeUp</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Todos os direitos de propriedade intelectual relativos à Plataforma, incluindo, sem limitação, softwares, código-fonte, design, marcas, logotipos, interfaces, algoritmos, metodologias, bases de dados e documentação, são de titularidade exclusiva da AmeUp ou de seus licenciantes. Nenhuma disposição destes Termos transfere ao Usuário qualquer direito de propriedade intelectual sobre a Plataforma.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">6.2 Licença de Uso</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A AmeUp concede ao Usuário uma licença limitada, não exclusiva, intransferível e revogável para acessar e utilizar a Plataforma exclusivamente para os fins previstos nestes Termos, durante a vigência do Plano contratado.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">6.3 Conteúdo do Usuário — Licença</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário concede à AmeUp uma licença não exclusiva, gratuita, irrevogável e mundial para usar, reproduzir, processar e exibir o Conteúdo do Usuário, exclusivamente para fins de prestação dos serviços e melhoria da Plataforma, respeitada a Política de Privacidade.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">6.4 Vedação de Uso da Marca</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário não poderá utilizar as marcas, logotipos, nomes comerciais ou qualquer outro sinal distintivo da AmeUp sem autorização prévia e por escrito, exceto para referenciar publicamente o uso da Plataforma (ex.: "Gerenciado com AmeUp"), de forma não enganosa.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 7. Privacidade e Proteção de Dados */}
                <section id="privacidade" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">7. Privacidade e Proteção de Dados (LGPD)</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">7.1 Política de Privacidade</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O tratamento de dados pessoais na Plataforma é regido pela Política de Privacidade da AmeUp, disponível em{' '}
                        <a href="/politica-de-privacidade" className="text-primary font-semibold underline hover:text-primary/80">
                          https://ameup.com.br/politica-de-privacidade
                        </a>, que integra estes Termos por referência. Ao aceitar estes Termos, o Usuário declara ter lido e concordado com a Política de Privacidade.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">7.2 Papéis no Tratamento de Dados</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Em relação aos dados dos Clientes Finais inseridos na Plataforma pelo Usuário: (i) o Usuário atua como Controlador dos dados; e (ii) a AmeUp atua como Operadora, processando os dados em nome e por instrução do Usuário. As obrigações de cada parte estão detalhadas na Política de Privacidade e, quando aplicável, em um Aditivo de Processamento de Dados (DPA) celebrado entre as partes.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">7.3 Medidas de Segurança</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A AmeUp adota medidas técnicas e organizacionais adequadas para proteger os dados tratados na Plataforma contra acessos não autorizados, perda, destruição ou divulgação indevida, em conformidade com a LGPD e as melhores práticas do setor.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">7.4 Incidentes de Segurança</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Em caso de incidente de segurança que afete dados pessoais, a AmeUp notificará o Usuário em prazo razoável, nos termos da LGPD, fornecendo as informações necessárias para que o Usuário possa cumprir suas obrigações regulatórias.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 8. Limitações de Responsabilidade */}
                <section id="limitacoes" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">8. Limitações de Responsabilidade e Garantias</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">8.1 Ausência de Garantias</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A Plataforma é fornecida "no estado em que se encontra" e "conforme disponível". A AmeUp não garante que a Plataforma seja livre de erros, interrupções, nem que atenda integralmente às expectativas específicas do Usuário.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">8.2 Limitação de Responsabilidade</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Na máxima extensão permitida pela legislação aplicável, a responsabilidade total da AmeUp perante o Usuário, por qualquer causa, estará limitada ao valor pago pelo Usuário à AmeUp nos 3 (três) meses imediatamente anteriores ao evento gerador da responsabilidade. A AmeUp não será responsável por: (i) lucros cessantes; (ii) perda de dados; (iii) danos indiretos ou consequenciais; (iv) danos decorrentes de falhas de serviços de terceiros.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">8.3 Indenização pelo Usuário</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário concorda em indenizar, defender e isentar a AmeUp e seus sócios, colaboradores, representantes e prestadores de serviço de quaisquer reclamações, danos, perdas e despesas (incluindo honorários advocatícios razoáveis) decorrentes de: (i) violação destes Termos; (ii) Conteúdo do Usuário; (iii) uso inadequado da Plataforma; ou (iv) violação de direitos de terceiros.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 9. Vigência, Suspensão e Rescisão */}
                <section id="vigencia" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">9. Vigência, Suspensão e Rescisão</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">9.1 Vigência</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Estes Termos vigoram pelo prazo indeterminado, a partir do cadastro do Usuário na Plataforma, podendo ser rescindidos por qualquer das partes conforme previsto nesta cláusula.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">9.2 Cancelamento pelo Usuário</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário pode cancelar sua assinatura a qualquer momento por meio das configurações da Conta ou pelo canal de suporte. O cancelamento não gera direito a reembolso de valores já cobrados. O acesso às funcionalidades do plano pago permanecerá disponível até o fim do período já pago.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">9.3 Suspensão ou Rescisão pela AmeUp</h3>
                      <p className="text-gray-700 mb-3">A AmeUp poderá suspender ou rescindir o acesso do Usuário imediatamente, sem aviso prévio, nos seguintes casos:</p>
                      <ul className="list-disc pl-6 space-y-2 text-gray-700">
                        <li>Violação de qualquer disposição destes Termos;</li>
                        <li>Inadimplência superior a 15 (quinze) dias;</li>
                        <li>Uso fraudulento ou ilícito da Plataforma;</li>
                        <li>Ações que causem danos à AmeUp ou a terceiros.</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">9.4 Efeitos da Rescisão</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Com a rescisão, o Usuário perderá o acesso à Plataforma e ao Conteúdo do Usuário nela armazenado. Recomenda-se que o Usuário exporte seus dados antes de solicitar o cancelamento. A AmeUp reterá os dados pelo prazo legal aplicável, conforme a Política de Privacidade.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 10. Cancelamento de Eventos pelo Cliente Final */}
                <section id="cancelamento" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">10. Cancelamento de Eventos pelo Cliente Final</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">10.1 Política de Cancelamento Configurável pelo Usuário</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A Plataforma AmeUp disponibiliza ao Usuário (fornecedor) uma funcionalidade específica para cadastro e configuração de sua própria política de cancelamento de eventos. Por meio dessa ferramenta, o Usuário poderá definir livremente as regras que regularão os cancelamentos solicitados por seus Clientes Finais, incluindo, entre outros parâmetros:
                      </p>
                      <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
                        <li>Prazos para cancelamento sem multa (ex.: até 30 dias antes do evento);</li>
                        <li>Percentuais de multa ou retenção progressivos por faixa de antecedência (ex.: 20% até 15 dias; 50% entre 7 e 14 dias; 100% com menos de 7 dias);</li>
                        <li>Valores mínimos de retenção para cobertura de custos operacionais já incorridos;</li>
                        <li>Política de reagendamento como alternativa ao cancelamento.</li>
                      </ul>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        As regras cadastradas pelo Usuário serão exibidas ao Cliente Final no momento da contratação do serviço e integrarão automaticamente o contrato digital gerado pela Plataforma. O Usuário é o único responsável pela definição, adequação e conformidade legal de sua política de cancelamento, sendo a AmeUp mera facilitadora tecnológica.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">10.2 Taxa da Operadora de Pagamento em Cancelamentos</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário declara estar ciente de que as operadoras de pagamento integradas à Plataforma (incluindo, mas não se limitando ao PagBank) não devolvem a taxa de processamento cobrada na transação original, mesmo nos casos em que o reembolso ao Cliente Final seja integral. Essa taxa é cobrada no ato do pagamento e fica retida pela operadora independentemente de qualquer cancelamento posterior. Exemplo:
                      </p>
                      <div className="bg-primary/5 p-4 rounded-lg border border-primary/20 mt-3">
                        <p className="text-gray-700 text-sm">
                          <strong>Reembolso pelo valor bruto (Usuário absorve a taxa):</strong> o Cliente Final recebe de volta o valor integral pago, e o Usuário arca com o custo da taxa da operadora. Exemplo: pagamento de R$ 100,00 com taxa de R$ 3,99 — o Cliente Final recebe R$ 100,00 e o Usuário perde os R$ 3,99.
                        </p>
                      </div>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        A AmeUp não se responsabiliza por conflitos entre Usuário e Cliente Final decorrentes da ausência ou ambiguidade nessa definição.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">10.3 Comissão AmeUp em Cancelamentos</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Nos casos em que ocorrer cancelamento do evento pelo Cliente Final e houver retenção parcial ou total de valores pelo Usuário (conforme a política de cancelamento configurada), a comissão devida à AmeUp incidirá sobre o valor efetivamente retido pelo Usuário, proporcionalmente ao percentual do Plano contratado. Sobre os valores integralmente reembolsados ao Cliente Final, não incidirá comissão da AmeUp.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">10.4 Processo de Cancelamento na Plataforma</h3>
                      <p className="text-gray-700 mb-3">Quando um Usuário solicitar o cancelamento de um evento por meio da Plataforma, o seguinte fluxo será aplicado:</p>
                      <ul className="list-disc pl-6 space-y-2 text-gray-700">
                        <li>A Plataforma calculará automaticamente o valor a ser reembolsado e o valor a ser retido, com base nas regras configuradas pelo Usuário;</li>
                        <li>O Usuário será notificado para confirmar ou contestar o cancelamento dentro do prazo definido em sua própria política;</li>
                        <li>Após confirmação, a operação de estorno junto à operadora de pagamento será iniciada, observados os prazos da própria operadora (que podem variar de 5 a 30 dias úteis);</li>
                        <li>Todo o histórico do cancelamento (solicitação, cálculo, confirmação e estorno) será registrado na Plataforma e estará disponível para consulta pelo Usuário.</li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">10.5 Responsabilidades e Limitações da AmeUp</h3>
                      <p className="text-gray-700 mb-3">A AmeUp atua exclusivamente como facilitadora tecnológica no processo de cancelamento, não sendo parte na relação contratual entre o Usuário e o Cliente Final. Nesse sentido:</p>
                      <ul className="list-disc pl-6 space-y-2 text-gray-700">
                        <li>A AmeUp não é responsável por disputas entre Usuário e Cliente Final relativas à política de cancelamento ou aos valores aplicados;</li>
                        <li>A AmeUp não se responsabiliza pelo prazo de processamento do estorno, que depende exclusivamente da operadora de pagamento e da bandeira do cartão utilizado pelo Cliente Final;</li>
                        <li>A taxa de processamento retida pela operadora de pagamento na transação original não é repassada à AmeUp e não poderá ser exigida da AmeUp em hipótese alguma;</li>
                        <li>A AmeUp não valida nem homologa a legalidade da política de cancelamento cadastrada pelo Usuário, sendo este o único responsável pela conformidade com o Código de Defesa do Consumidor (Lei 8.078/1990) e demais normas aplicáveis.</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* 11. Alterações nos Termos */}
                <section id="alteracoes" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">11. Alterações nos Termos</h2>
                  <p className="text-gray-700 leading-relaxed">
                    A AmeUp se reserva o direito de alterar estes Termos a qualquer momento. Alterações relevantes serão comunicadas ao Usuário com antecedência mínima de 15 (quinze) dias, por e-mail cadastrado ou por notificação na Plataforma. O uso continuado da Plataforma após a entrada em vigor das alterações implica a aceitação dos novos Termos. Caso o Usuário não concorde, deverá cancelar sua assinatura antes da vigência das alterações.
                  </p>
                </section>

                {/* 12. Disposições Gerais */}
                <section id="disposicoes" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">12. Disposições Gerais</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">12.1 Legislação Aplicável</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Estes Termos são regidos pelas leis da República Federativa do Brasil, em especial pelo Código Civil, pelo Marco Civil da Internet (Lei 12.965/2014), pela Lei Geral de Proteção de Dados (Lei 13.709/2018) e pela legislação consumerista aplicável.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">12.2 Foro</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Para a resolução de conflitos decorrentes destes Termos, as partes elegem o foro da Comarca de São Paulo/SP, com exclusão de qualquer outro, por mais privilegiado que seja.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">12.3 Independência das Cláusulas</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Se qualquer cláusula destes Termos for considerada nula, inválida ou inaplicável, as demais cláusulas permanecerão em pleno vigor e efeito.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">12.4 Ausência de Renúncia</h3>
                      <p className="text-gray-700 leading-relaxed">
                        A omissão da AmeUp em exigir o cumprimento de qualquer disposição destes Termos não constituirá renúncia ao direito de fazê-lo no futuro.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">12.5 Cessão</h3>
                      <p className="text-gray-700 leading-relaxed">
                        O Usuário não poderá ceder ou transferir seus direitos ou obrigações decorrentes destes Termos sem o consentimento prévio e por escrito da AmeUp. A AmeUp poderá ceder seus direitos e obrigações a qualquer entidade do mesmo grupo econômico ou em caso de fusão, aquisição ou reestruturação societária.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">12.6 Comunicações</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Todas as comunicações oficiais entre as partes serão realizadas: (i) pela AmeUp, por e-mail cadastrado pelo Usuário ou por notificação na Plataforma; (ii) pelo Usuário, por meio dos canais de suporte disponíveis em www.ameup.com.br/suporte.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3 text-gray-800">12.7 Acordo Integral</h3>
                      <p className="text-gray-700 leading-relaxed">
                        Estes Termos, juntamente com a Política de Privacidade e demais políticas publicadas pela AmeUp, constituem o acordo integral entre as partes sobre o objeto aqui descrito, substituindo quaisquer entendimentos anteriores, verbais ou escritos.
                      </p>
                    </div>
                  </div>
                </section>

                {/* 13. Aceite e Consentimento */}
                <section id="aceite" className="border-l-4 border-primary pl-4 scroll-mt-24">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800">13. Aceite e Consentimento</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Ao criar uma Conta na Plataforma AmeUp, o Usuário declara expressamente ter lido, compreendido e concordado com todos os termos e condições aqui estabelecidos, bem como com a Política de Privacidade da AmeUp.
                  </p>
                  <div className="mt-8 text-center text-gray-600">
                    <p className="font-semibold">AmeUp Tecnologia Ltda.</p>
                    <p>www.ameup.com.br | @ameupbr</p>
                    <p>São Paulo – SP, Brasil</p>
                  </div>
                </section>

              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer hidePaymentMethods={true} />
    </div>
  );
};

export default TermosDeUso;
